/*
Side-by-side species comparison.

Reached from a long-press on an Arboretum card → ComparePicker → tap
a second species. Renders two columns sharing the layout: identity,
Paris-wide stats, Wikipédia summary (truncated), top 3
arrondissements, and a strip of the user's photos for that species.

The columns scroll independently on tablet+ so a long Wikipédia
summary on one side doesn't push the other off-screen. On phones we
stack vertically (CSS @media break) — no horizontal layout wins on
narrow widths.
*/

import { navigate, kPaths, type Route } from '../router'
import { ensureSpeciesIndex, speciesNameFor, type SpeciesName } from '../data/speciesIndex'
import { ensureSpeciesInfo, speciesInfoFor, type SpeciesEntry } from '../data/speciesInfo'
import { listCaptures } from '../data/captureRepository'
import { loadPhoto } from '../data/photoStore'
import type { Capture } from '../data/types'

const kFrenchNumber = new Intl.NumberFormat('fr-FR')
const kPercentFr = new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 2 })
const kSummaryMaxChars: number = 320  // truncate Wikipédia summaries past this so both columns balance
const kPhotoStripCap: number = 6  // most-recent photos per species shown in the comparison

interface ColumnData
{
	sk: number
	name: SpeciesName | null
	info: SpeciesEntry | null
	captures: Capture[]
}

let fActiveObjectUrls: string[] = []  // photo blob URLs created by the current mount, revoked on remount

/*
Mount the screen. Cleans up any photo Object URLs from a previous
mount, renders the static shell, then hydrates both columns in
parallel.
*/
export function mountCompareScreen(inRoot: HTMLElement, inRoute: Route): void
{
	revokeObjectUrls()
	if (inRoute.id !== 'compare')
	{
		inRoot.innerHTML = '<div class="screen"><div class="placeholder">Route invalide.</div></div>'
		return
	}
	const vSkA = inRoute.skA
	const vSkB = inRoute.skB

	inRoot.innerHTML = renderShell()
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => {
		if (history.length > 1) history.back()
		else navigate(kPaths.arboretum())
	})
	void hydrate(inRoot, vSkA, vSkB)
}

function revokeObjectUrls(): void
{
	for (const vUrl of fActiveObjectUrls) URL.revokeObjectURL(vUrl)
	fActiveObjectUrls = []
}

function renderShell(): string
{
	return `
		<div class="screen compare-screen">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Comparer</h1>
			</header>
			<div class="compare-grid">
				<section class="compare-column" id="compare-column-a">
					<div class="placeholder">Chargement…</div>
				</section>
				<section class="compare-column" id="compare-column-b">
					<div class="placeholder">Chargement…</div>
				</section>
			</div>
		</div>
	`
}

async function hydrate(inRoot: HTMLElement, inSkA: number, inSkB: number): Promise<void>
{
	const vColA = inRoot.querySelector<HTMLElement>('#compare-column-a')
	const vColB = inRoot.querySelector<HTMLElement>('#compare-column-b')
	if (!vColA || !vColB) return
	try
	{
		// Pre-warm both indices in parallel; their result types feed
		// the speciesNameFor / speciesInfoFor synchronous accessors.
		const [, , vAllCaptures] = await Promise.all([
			ensureSpeciesIndex(),
			ensureSpeciesInfo(),
			listCaptures(),
		])
		const vDataA = buildColumnData(inSkA, vAllCaptures)
		const vDataB = buildColumnData(inSkB, vAllCaptures)
		vColA.innerHTML = renderColumn(vDataA)
		vColB.innerHTML = renderColumn(vDataB)
		void hydratePhotoStrip(vColA, vDataA)
		void hydratePhotoStrip(vColB, vDataB)
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		vColA.innerHTML = `<div class="placeholder species-error">⚠ ${escapeHtml(vMsg)}</div>`
		vColB.innerHTML = ''
	}
}

function buildColumnData(inSk: number, inAllCaptures: Capture[]): ColumnData
{
	return {
		sk: inSk,
		name: speciesNameFor(inSk),
		info: speciesInfoFor(inSk),
		captures: inAllCaptures.filter((inC) => inC.speciesIndex === inSk),
	}
}

/*
Render one column's static markup. The photo strip slot is filled
asynchronously by `hydratePhotoStrip`; everything else paints from
the synchronously-loaded indices.
*/
function renderColumn(inData: ColumnData): string
{
	const vTitle = inData.name?.commonName ?? formatLatin(inData.name)
	const vSubtitle = inData.name?.commonName ? formatLatin(inData.name) : ''
	const vCount = inData.info?.stats.count
	const vProp = inData.info?.stats.proportion
	const vMedHm = inData.info?.stats.medianHm
	const vMedCm = inData.info?.stats.medianCircCm
	const vTopArr = inData.info?.stats.topArrAbs.slice(0, 3) ?? []

	const vSummary = truncate(inData.info?.summary ?? '', kSummaryMaxChars)
	const vWikiSlug = inData.info?.wp ?? ''

	const vMedians: string[] = []
	if (typeof vMedHm === 'number' && vMedHm > 0)
	{
		vMedians.push(`<div class="compare-stat-line">📏 Hauteur médiane : <strong>${vMedHm}&nbsp;m</strong></div>`)
	}
	if (typeof vMedCm === 'number' && vMedCm > 0)
	{
		vMedians.push(`<div class="compare-stat-line">⭕ Circonf. médiane : <strong>${vMedCm}&nbsp;cm</strong></div>`)
	}

	return `
		<div class="compare-column-inner">
			<header class="compare-column-header">
				<div class="compare-column-glyph" aria-hidden="true">🌳</div>
				<h2 class="compare-column-title display" title="${escapeAttr(vTitle)}">${escapeHtml(vTitle)}</h2>
				${vSubtitle ? `<div class="compare-column-subtitle"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
				<button class="compare-column-link" type="button" data-sk="${inData.sk}" aria-label="Ouvrir la fiche complète">Voir la fiche →</button>
			</header>
			<section class="compare-section">
				<h3 class="compare-section-title">À Paris</h3>
				${typeof vCount === 'number'
					? `<div class="compare-stat-line"><strong>${kFrenchNumber.format(vCount)}</strong> arbres · ${kPercentFr.format(vProp ?? 0)}</div>`
					: '<div class="compare-stat-line">—</div>'}
				${vMedians.join('')}
			</section>
			<section class="compare-section">
				<h3 class="compare-section-title">Wikipédia</h3>
				${vSummary
					? `<p class="compare-summary">${escapeHtml(vSummary)}</p>${vWikiSlug ? `<a class="compare-link" href="https://fr.wikipedia.org/wiki/${encodeURIComponent(vWikiSlug)}" target="_blank" rel="noopener">Lire la suite →</a>` : ''}`
					: '<p class="compare-summary compare-summary-missing">Pas de résumé disponible.</p>'}
			</section>
			<section class="compare-section">
				<h3 class="compare-section-title">Top arrondissements</h3>
				${vTopArr.length > 0
					? `<ul class="compare-arr-list">${vTopArr.map((inA) => `<li><span class="compare-arr-label">${escapeHtml(inA.arr)}</span><span class="compare-arr-count">${kFrenchNumber.format(inA.count)}</span></li>`).join('')}</ul>`
					: '<div class="compare-stat-line">—</div>'}
			</section>
			<section class="compare-section">
				<h3 class="compare-section-title">Tes captures · ${inData.captures.length}</h3>
				<div class="compare-photo-strip" data-sk="${inData.sk}">
					${inData.captures.length === 0 ? '<div class="compare-stat-line">—</div>' : ''}
				</div>
			</section>
		</div>
	`
}

async function hydratePhotoStrip(inColumn: HTMLElement, inData: ColumnData): Promise<void>
{
	const vStrip = inColumn.querySelector<HTMLElement>(`.compare-photo-strip[data-sk="${inData.sk}"]`)
	if (!vStrip) return
	const vSorted = inData.captures.slice().sort((inA, inB) => inB.timestamp - inA.timestamp).slice(0, kPhotoStripCap)
	for (const vCap of vSorted)
	{
		if (!vCap.photoFilename) continue
		try
		{
			const vBlob = await loadPhoto(vCap.photoFilename)
			if (!vBlob) continue
			const vUrl = URL.createObjectURL(vBlob)
			fActiveObjectUrls.push(vUrl)
			const vImg = document.createElement('img')
			vImg.alt = ''
			vImg.loading = 'lazy'
			vImg.src = vUrl
			vImg.className = 'compare-photo-thumb'
			vStrip.appendChild(vImg)
		}
		catch
		{
			// Soft fail — keep the strip building.
		}
	}

	// Wire the "Voir la fiche →" link inside this column. We attach it
	// here (rather than at render time) because the column markup is
	// rebuilt on every hydrate and per-column event listeners need to
	// re-bind to the fresh DOM.
	inColumn.querySelector<HTMLElement>(`.compare-column-link[data-sk="${inData.sk}"]`)?.addEventListener('click', () => {
		navigate(kPaths.species(inData.sk))
	})
}

function truncate(inText: string, inMax: number): string
{
	if (inText.length <= inMax) return inText
	const vSlice = inText.slice(0, inMax)
	const vLastBreak = Math.max(vSlice.lastIndexOf(' '), vSlice.lastIndexOf('. '))
	const vCut = vLastBreak > inMax * 0.6 ? vSlice.slice(0, vLastBreak) : vSlice
	return `${vCut.trimEnd()}…`
}

function formatLatin(inName: SpeciesName | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
